import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  H2,
  ModalContainer,
  Spinner,
  TextInput,
  useDisclosure,
} from '@ouster/webviz-react-components';

import { FormEvent, useEffect, useState } from 'react';
import ProvisionedOrganization from 'src/components/adminDashboard/provision/ProvisionedOrganization';
import ProvisioningOrganization from 'src/components/adminDashboard/provision/ProvisioningOrganization';
import { useStoreActions, useStoreState } from 'src/reducers';

const CreateProvisionOrganizationModal = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const { ousterUserCreateOrg } = useStoreActions(
    (actions) => actions.organization
  );
  const { getProvisionedOrganizations } = useStoreActions(
    (actions) => actions.adminDashboard
  );

  const [orgName, setOrgName] = useState('');

  const handleCreateOrg = async (e: FormEvent) => {
    e.preventDefault();
    await ousterUserCreateOrg({ orgName, is_provisioned: true });
    onClose();
    getProvisionedOrganizations();
  };

  return (
    <Box width={480}>
      <form
        onSubmit={(e) => {
          handleCreateOrg(e);
        }}
        id="provision-organization-form"
        data-testid="provision-organization-form"
      >
        <FormControl paddingInline={0}>
          <FormLabel>Provision Organization Name</FormLabel>
          <TextInput
            type="text"
            isRequired
            name="organizationName"
            flex={1}
            placeholder="Organization Name"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            data-testid="provision-organization-name-input"
          />
          <Box display="flex" justifyContent="flex-end" gap={2} marginTop={8}>
            <Button
              colorScheme="gray"
              minWidth={150}
              type="button"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button minWidth={150} type="submit" disabled={!orgName}>
              Create
            </Button>
          </Box>
        </FormControl>
      </form>
    </Box>
  );
};

export default function OrganizationProvision() {
  const {
    isOpen: isProvisionModalOpen,
    onOpen: onProvisionModalOpen,
    onClose: onProvisionModalClose,
  } = useDisclosure();

  const { getProvisionedOrganizations } = useStoreActions(
    (actions) => actions.adminDashboard
  );
  const { provisionedOrganizations, isLoadingProvisionedOrganizations } =
    useStoreState((storeState) => storeState.adminDashboard);
  const [isRefreshingProvisionedOrgs, setIsRefreshingProvisionedOrgs] =
    useState(false);

  useEffect(() => {
    getProvisionedOrganizations();
  }, [getProvisionedOrganizations]);

  const onRefreshProvisionedOrgs = async () => {
    if (isRefreshingProvisionedOrgs || isLoadingProvisionedOrganizations) {
      return;
    }

    setIsRefreshingProvisionedOrgs(true);
    try {
      await getProvisionedOrganizations();
    } finally {
      setIsRefreshingProvisionedOrgs(false);
    }
  };

  const provisioningOrgs =
    provisionedOrganizations?.filter((org) => org.is_provisioned) ?? [];

  const provisionedOrgs =
    provisionedOrganizations?.filter((org) => !org.is_provisioned) ?? [];

  if (isLoadingProvisionedOrganizations) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap={4}
        h="full"
      >
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <>
      <Box
        h="full"
        p="25px 36px 0px"
        display="flex"
        flexDirection="column"
        gap={8}
      >
        <Flex gap={4} alignItems="center" justifyContent="space-between">
          <H2 fontWeight="medium" fontSize={30}>
            Provisioning Manager
          </H2>
          <Flex gap={2} alignItems="center">
            <Button
              onClick={onRefreshProvisionedOrgs}
              variant="outline"
              minWidth="min-content"
              data-testid="refresh-provision-organizations-button"
              leftIcon="refresh"
              disabled={
                isRefreshingProvisionedOrgs || isLoadingProvisionedOrganizations
              }
            >
              Refresh List
            </Button>
            <Button
              onClick={onProvisionModalOpen}
              minWidth="min-content"
              leftIcon="add"
              data-testid="provision-organization-button"
              width={240}
              mr={2}
            >
              Provision Organization
            </Button>
          </Flex>
        </Flex>

        <ProvisioningOrganization provisioningOrgs={provisioningOrgs} />

        <ProvisionedOrganization provisionedOrgs={provisionedOrgs} />
      </Box>
      <ModalContainer
        isOpen={isProvisionModalOpen}
        onClose={onProvisionModalClose}
        header="Provision Organization"
        footer={null}
        content={
          <CreateProvisionOrganizationModal onClose={onProvisionModalClose} />
        }
        data-testid="provision-organization-modal"
      />
    </>
  );
}
